import * as analyticsServer from 'src/internal/analytics/analytics.server';
import buildParams from 'src/internal/analytics/buildParams';

import type {
  IAnalytics,
  ImperativeAnalytics,
} from 'src/internal/analytics/types';

type AnalyticsMethodName = keyof IAnalytics;

const method = <TMethod extends AnalyticsMethodName>(
  analyticsMethodName: TMethod,
): ImperativeAnalytics[TMethod] => {
  return (config, eventName, parameters): void => {
    if (!config || config.analytics?.disabled) {
      return;
    }

    const injectedMethod =
      config._internal_observability?.analytics?.[analyticsMethodName];

    if (injectedMethod) {
      injectedMethod(config, eventName, buildParams(config, parameters));
      return;
    }

    try {
      analyticsServer[analyticsMethodName](config, eventName, parameters);
    } catch (error) {
      // The errors here are unexpected. But if they happen, degrade gracefully:
      // - Don't crash the code calling @sail/observability APIs
      // - Report an unhandled exception so that it is reported to Sentry
      Promise.reject(error);
    }
  };
};

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/useAnalytics#action
 */
export const action = method('action');

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/useAnalytics#link
 */
export const link = method('link');

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/useAnalytics#modal
 */
export const modal = method('modal');

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/useAnalytics#track
 */
export const track = method('track');

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/useAnalytics#viewed
 */
export const viewed = method('viewed');
